import { defineComponent, computed, ref, inject, watch, resolveComponent, openBlock, createElementBlock, normalizeClass, createBlock, createCommentVNode, createElementVNode, withDirectives, mergeProps, vModelDynamic, toDisplayString, withModifiers } from 'vue';
import Label from '@ingka/label-vue';
import SkapaButton from '@ingka/button-vue';
import visibilityHide from '@ingka/icons-shared/paths/visibility-hide';
import visibilityShow from '@ingka/icons-shared/paths/visibility-show';
import SSRIcon from '@ingka/ssr-icon-vue';

var script = defineComponent({
    components: {
        SkapaButton,
        Label,
        SSRIcon
    },
    inheritAttrs: false,
    props: {
        id: {
            type: String,
            default: '',
            required: true
        },
        label: {
            type: String,
            default: 'Label'
        },
        placeholder: {
            type: String,
            default: undefined
        },
        req: {
            type: [String],
            default: ''
        },
        type: {
            type: String,
            default: 'text',
            required: true
        },
        disabled: {
            type: Boolean,
            default: false
        },
        iconClickDescription: {
            type: String,
            default: undefined
        },
        iconPosition: {
            type: String,
            default: undefined
        },
        iconOnClick: {
            type: Function,
            default: undefined
        },
        onChange: {
            type: Function,
            default: undefined
        },
        prefix: {
            type: String,
            default: ''
        },
        hidePasswordText: {
            type: String,
            default: 'Hide password'
        },
        showPasswordText: {
            type: String,
            default: 'Show password'
        },
        ssrIcon: {
            type: Array,
            default: () => []
        },
        prefixLabel: {
            type: String,
            default: ''
        },
        suffixLabel: {
            type: String,
            default: ''
        },
        inputMode: {
            type: String,
            default: undefined
        },
        modelValue: {
            type: String,
            default: undefined
        },
        value: {
            type: String,
            default: undefined
        }
    },
    emits: ['update:modelValue'],
    setup(props, { emit }) {
        var _a;
        const trailing = computed(() => props.type === 'password' || props.iconPosition === 'trailing');
        const convertedType = computed(() => props.type === 'number' ? 'text' : props.type);
        let passwordVisible = ref(false);
        const valid = inject('valid', true);
        const shouldValidate = inject('shouldValidate', false);
        const characterCount = inject('characterCount', null);
        const describedById = inject('validationMessageId', undefined);
        const inputContent = ref(((_a = (props.modelValue || props.value)) === null || _a === void 0 ? void 0 : _a.toString()) || '');
        if (characterCount)
            characterCount.value = (inputContent.value.length) || 0;
        watch([() => props.modelValue, () => props.value], () => {
            // if component is controlled and the control properties change
            if (props.modelValue)
                inputContent.value = props.modelValue;
            else if (props.value)
                inputContent.value = props.value;
        });
        const onChangeHandler = (event) => {
            if (characterCount) {
                characterCount.value = inputContent.value.toString().length;
            }
            emit('update:modelValue', inputContent.value);
            if (props.onChange) {
                props.onChange(event);
            }
        };
        const togglePasswordVisibility = () => {
            passwordVisible.value = !passwordVisible.value;
        };
        return { inputContent, trailing, convertedType, onChangeHandler, valid, shouldValidate, describedById, togglePasswordVisibility, passwordVisible, visibilityHide, visibilityShow };
    }
});

const _hoisted_1 = ["id", "type", "disabled", "required", "inputMode", "aria-describedby", "aria-required", "aria-invalid"];

function render(_ctx, _cache, $props, $setup, $data, $options) {
  const _component_Label = resolveComponent("Label");
  const _component_SSRIcon = resolveComponent("SSRIcon");
  const _component_SkapaButton = resolveComponent("SkapaButton");

  return (openBlock(), createElementBlock("div", {
    class: normalizeClass([
      `${_ctx.prefix}input-field`,
      {
        [`${_ctx.prefix}input-field--leading-icon`]: _ctx.iconPosition === 'leading' && _ctx.ssrIcon.length > 0,
        [`${_ctx.prefix}input-field--trailing-icon`]: _ctx.trailing && _ctx.ssrIcon.length > 0 || _ctx.type === 'password',
        [`${_ctx.prefix}input-field--disabled`]: _ctx.disabled,
        [`${_ctx.prefix}input-field--prefixed`]: _ctx.prefixLabel,
        [`${_ctx.prefix}input-field--suffixed`]: _ctx.suffixLabel,
        [`${_ctx.prefix}input-field--labelled`]: _ctx.inputContent !== '',
        [`${_ctx.prefix}input-field--error`]: _ctx.shouldValidate && !_ctx.valid,
        [`${_ctx.prefix}input-field--success`]: _ctx.shouldValidate && _ctx.valid
      },
      `${_ctx.prefix}label-wrapper ${_ctx.prefix}label-wrapper--text-input`,
      _ctx.$attrs.class
    ])
  }, [
    (_ctx.label)
      ? (openBlock(), createBlock(_component_Label, {
          key: 0,
          title: _ctx.label,
          prefix: _ctx.prefix,
          "html-for": _ctx.id,
          label: _ctx.label
        }, null, 8 /* PROPS */, ["title", "prefix", "html-for", "label"]))
      : createCommentVNode("v-if", true),
    createElementVNode("div", {
      class: normalizeClass(`${_ctx.prefix}input-field__wrapper`)
    }, [
      withDirectives(createElementVNode("input", mergeProps({
        id: _ctx.id,
        "onUpdate:modelValue": _cache[0] || (_cache[0] = $event => ((_ctx.inputContent) = $event)),
        type: _ctx.passwordVisible ? 'text' : _ctx.convertedType,
        disabled: _ctx.disabled,
        required: Boolean(_ctx.req)
      }, _ctx.$attrs, {
        inputMode: _ctx.type === 'number' ? 'numeric' : _ctx.inputMode,
        "aria-describedby": _ctx.describedById,
        "aria-required": Boolean(_ctx.req),
        "aria-invalid": _ctx.shouldValidate ? !_ctx.valid : undefined,
        onInput: _cache[1] || (_cache[1] = (...args) => (_ctx.onChangeHandler && _ctx.onChangeHandler(...args)))
      }), null, 16 /* FULL_PROPS */, _hoisted_1), [
        [vModelDynamic, _ctx.inputContent]
      ]),
      ((_ctx.iconPosition === 'leading' && _ctx.ssrIcon.length > 0))
        ? (openBlock(), createBlock(_component_SSRIcon, {
            key: 0,
            prefix: _ctx.prefix,
            paths: _ctx.ssrIcon
          }, null, 8 /* PROPS */, ["prefix", "paths"]))
        : createCommentVNode("v-if", true),
      (_ctx.prefixLabel)
        ? (openBlock(), createElementBlock("span", {
            key: 1,
            class: normalizeClass(`${_ctx.prefix}input-field__prefix-label`)
          }, toDisplayString(_ctx.prefixLabel), 3 /* TEXT, CLASS */))
        : createCommentVNode("v-if", true),
      (_ctx.suffixLabel)
        ? (openBlock(), createElementBlock("span", {
            key: 2,
            class: normalizeClass(`${_ctx.prefix}input-field__suffix-label`)
          }, toDisplayString(_ctx.suffixLabel), 3 /* TEXT, CLASS */))
        : createCommentVNode("v-if", true),
      (_ctx.type === 'password')
        ? (openBlock(), createBlock(_component_SkapaButton, {
            key: 3,
            prefix: _ctx.prefix,
            "html-type": "button",
            type: "tertiary",
            "icon-only": "",
            "xsmall-icon-only": "",
            "ssr-icon": _ctx.passwordVisible ? _ctx.visibilityHide : _ctx.visibilityShow,
            text: _ctx.passwordVisible ? _ctx.hidePasswordText : _ctx.showPasswordText,
            disabled: _ctx.disabled,
            onClick: withModifiers(_ctx.togglePasswordVisibility, ["prevent"])
          }, null, 8 /* PROPS */, ["prefix", "ssr-icon", "text", "disabled", "onClick"]))
        : createCommentVNode("v-if", true),
      ((_ctx.iconPosition === 'trailing' && _ctx.ssrIcon.length > 0))
        ? (openBlock(), createBlock(_component_SkapaButton, {
            key: 4,
            "html-type": "button",
            prefix: _ctx.prefix,
            "xsmall-icon-only": "",
            "ssr-icon": _ctx.ssrIcon,
            "icon-only": "",
            type: "tertiary",
            text: _ctx.iconClickDescription,
            disabled: _ctx.disabled,
            onClick: _ctx.iconOnClick
          }, null, 8 /* PROPS */, ["prefix", "ssr-icon", "text", "disabled", "onClick"]))
        : createCommentVNode("v-if", true),
      createElementVNode("span", {
        class: normalizeClass(`${_ctx.prefix}input-field__border`)
      }, null, 2 /* CLASS */)
    ], 2 /* CLASS */)
  ], 2 /* CLASS */))
}

script.render = render;
script.__file = "packages/vue/input-field/lib/InputField.vue";

export { script as default };
